'use client';

import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import type { SourceAudio } from '@/app/types';
import { cn, getVoiceCloneWatermarkText } from '@/app/utils';
import { formatAudioDuration, getAudioFileName } from '../utils';

/** 后端/上传返回的内部资源 URI，不能作为浏览器 <audio> 的播放地址 */
function isInternalMediaUri(url: string): boolean {
  return url.startsWith('ssupload:') || url.startsWith('sscreation:');
}

function getPlayableUrl(url: string | undefined): string {
  if (url && !isInternalMediaUri(url)) {
    return url;
  }
  return '';
}

function getBrowserAudioSrc(audio: SourceAudio): string {
  const fromSrc = getPlayableUrl(audio.src);
  if (fromSrc) return fromSrc;
  return getPlayableUrl(audio.preview);
}

interface AudioPlayerProps {
  audio: SourceAudio;
  onDelete?: () => void;
  onEdit?: () => void;
}

export function AudioPlayer({ audio, onDelete, onEdit }: AudioPlayerProps) {
  const t = useTranslations();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioSrc = getBrowserAudioSrc(audio);
  const canPlay = audioSrc.length > 0;

  useEffect(() => {
    if (canPlay) return;
    audioRef.current?.pause();
    setIsPlaying(false);
  }, [canPlay]);

  const handlePlayPause = () => {
    if (!canPlay || !audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      void audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const shouldShowWatermark = !!onEdit;

  return (
    <div className="flex h-[58px] items-center gap-3 rounded-16 border border-system-white08 bg-system-white04 px-4 backdrop-blur-[30px]">
      {/* biome-ignore lint/a11y/useMediaCaption: User recording playback does not require captions */}
      <audio
        ref={audioRef}
        src={audioSrc}
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />
      <button
        type="button"
        onClick={handlePlayPause}
        disabled={!canPlay}
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full bg-system-white text-[#111217] transition',
          canPlay ? 'hover:scale-105' : 'cursor-not-allowed opacity-40',
        )}
        aria-label={isPlaying ? t('tools.voiceClone.player.pause') : t('tools.voiceClone.player.play')}
      >
        {isPlaying ? <PauseIcon className="text-2xl" /> : <PlayIcon className="ml-0.5 text-2xl" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2 font-semibold text-[13px] leading-[1.3]">
          <span className="truncate text-system-white">{getAudioFileName(audio)}</span>
          {shouldShowWatermark && (
            <span className="shrink-0 font-normal text-[12px] text-system-white/40">
              {getVoiceCloneWatermarkText()}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[11px] text-system-white/40 leading-[1.2]">
          {formatAudioDuration(audio.duration ?? 0)}
        </div>
      </div>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="flex size-7 shrink-0 items-center justify-center rounded-full text-system-white/72 transition hover:bg-system-white08 hover:text-system-white"
          aria-label={t('tools.voiceClone.player.delete')}
        >
          <BaseLineClose className="text-base" />
        </button>
      )}
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="flex size-7 shrink-0 items-center justify-center rounded-full text-system-white/72 transition hover:bg-system-white08 hover:text-system-white"
          aria-label={t('tools.voiceClone.voiceInfo.edit')}
        >
          <EditIcon className="text-base" />
        </button>
      )}
    </div>
  );
}

function PlayIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 16 16" fill="none" aria-hidden="true" {...props}>
      <path d="M5.5 3.75v8.5L12 8 5.5 3.75Z" fill="currentColor" />
    </svg>
  );
}

function PauseIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 16 16" fill="none" aria-hidden="true" {...props}>
      <path d="M5 4h2v8H5V4Zm4 0h2v8H9V4Z" fill="currentColor" />
    </svg>
  );
}

function EditIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 16 16" fill="none" aria-hidden="true" {...props}>
      <path
        d="m9.95 3.08 2.97 2.97M2.67 13.33l3.12-.7 7.3-7.3a1.4 1.4 0 0 0-1.98-1.98l-7.3 7.3-.7 3.12Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.2"
      />
    </svg>
  );
}
